// ═══════════════════════════════════════════════════════════════════════
//  DIRECTIVES: class-*, style-*
// ═══════════════════════════════════════════════════════════════════════

import { _watchExpr } from "../globals.js";
import { evaluate } from "../evaluate.js";
import { findContext } from "../dom.js";
import { registerDirective } from "../registry.js";
import { _watchI18n } from "../globals.js";

registerDirective("class-*", {
  priority: 20,
  init(el, name, expr) {
    const suffix = name.replace("class-", "");
    const ctx = findContext(el);

    // class-map="{ active: x, bold: y }"
    // Supports space-separated keys: class-map="{ 'bg-sky-500 text-white': x }"
    if (suffix === "map") {
      function update() {
        const obj = evaluate(expr, ctx);
        if (obj && typeof obj === "object") {
          for (const [cls, cond] of Object.entries(obj)) {
            const parts = cls.split(/\s+/).filter(Boolean);
            parts.forEach((c) => el.classList.toggle(c, !!cond));
          }
        }
      }
      _watchExpr(expr, ctx, update);
      update();
      return;
    }

    // class-list="['a', condition ? 'b' : 'c']"
    if (suffix === "list") {
      let prevClasses = [];
      function update() {
        const arr = evaluate(expr, ctx);
        if (Array.isArray(arr)) {
          prevClasses.forEach((cls) => {
            if (cls) el.classList.remove(cls);
          });
          const next = arr.filter(Boolean);
          next.forEach((cls) => el.classList.add(cls));
          prevClasses = next;
        }
      }
      _watchExpr(expr, ctx, update);
      update();
      return;
    }

    // class-{name}="expr"
    // Boolean memo: unchanged condition = no classList touch (null ≠ any
    // boolean, so the first run always writes). The skip also requires the
    // live classList to agree — external code can add/remove the class
    // between updates, and a memo-only skip would never resync it.
    let last = null;
    function update() {
      const on = !!evaluate(expr, ctx);
      if (on === last && el.classList.contains(suffix) === on) return;
      last = on;
      el.classList.toggle(suffix, on);
    }
    _watchExpr(expr, ctx, update);
    if (expr.includes("NoJS.locale") || expr.includes("window.NoJS.locale")) _watchI18n(update);
    update();
  },
});

registerDirective("style-*", {
  priority: 20,
  init(el, name, expr) {
    const suffix = name.replace("style-", "");
    const ctx = findContext(el);

    // style-map="{ color: x, fontSize: y }"
    if (suffix === "map") {
      let prevProps = [];
      function update() {
        const obj = evaluate(expr, ctx);
        if (obj && typeof obj === "object") {
          const nextProps = Object.keys(obj);
          // Clear properties that were set previously but are absent now, so a
          // property removed from the bound object no longer lingers on the
          // element (stale style leak).
          for (const prop of prevProps) {
            if (!(prop in obj)) {
              if (prop.startsWith("--")) el.style.removeProperty(prop);
              else el.style[prop] = "";
            }
          }
          for (const [prop, val] of Object.entries(obj)) {
            if (prop.startsWith("--")) el.style.setProperty(prop, val ?? "");
            else el.style[prop] = val ?? "";
          }
          prevProps = nextProps;
        }
      }
      _watchExpr(expr, ctx, update);
      update();
      return;
    }

    // style-{property}="expr" (e.g. style-color, style-font-size)
    // CSS custom properties: style---primary-color -> suffix "--primary-color"
    // Must use setProperty() — bracket assignment is an inert JS expando.
    const isCustomProp = suffix.startsWith("--");
    const cssProp = isCustomProp ? suffix : suffix.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
    // String memo: unchanged value = no style write. The skip also requires
    // the live inline style to agree — external code can overwrite it, and
    // a memo-only skip would never resync. Compare via getPropertyValue on
    // the hyphenated suffix (works for both custom and standard properties);
    // CSSOM value normalization can make the compare fail spuriously, which
    // only costs an idempotent re-write, never a stale skip.
    let last;
    function update() {
      const val = evaluate(expr, ctx);
      const v = val != null ? String(val) : "";
      if (v === last && el.style.getPropertyValue(suffix) === v) return;
      last = v;
      if (isCustomProp) el.style.setProperty(cssProp, v);
      else el.style[cssProp] = v;
    }
    _watchExpr(expr, ctx, update);
    update();
  },
});
